import { useEffect, useRef, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog/AppDialog';
import { describeRecurrence, type Ends, type Frequency, type Recurrence } from './builderApi';

const FREQS: Frequency[] = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Last'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SUMMARY_DELAY_MS = 200;

/** The popup's starting point when the template has no schedule (prototype: Daily, every 1 day, no end). */
const FRESH: Recurrence = {
  frequency: 'Daily', interval: 1, weekDays: ['Mon'], pattern: 'day', dayNumber: 1, ordinal: 'First',
  month: 1, ends: 'never', occurrences: 10, endBy: null,
};

interface Props {
  value: Recurrence | null;
  startDate: string | null;
  readOnly?: boolean;
  onCancel: () => void;
  /** null = Remove recurrence. */
  onApply: (r: Recurrence | null, summary: string) => void;
}

/**
 * Recurrence Setup (prototype ALPACA_Daily_QC_Setup_v20.html): Daily / Weekly / Monthly /
 * Yearly with their rules, how it ends, and a live "Schedule:" summary — the summary and the
 * checks come from the API (DailyQcRecurrence), which also writes G5's RecurrenceInfoXml.
 */
export default function BuilderRecurrenceDialog({ value, startDate, readOnly, onCancel, onApply }: Props) {
  const [r, setR] = useState<Recurrence>(() => normalise(value ?? FRESH));
  const [summary, setSummary] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    const n = ++seq.current;
    const t = setTimeout(() => {
      describeRecurrence(r, startDate)
        .then((d) => { if (n === seq.current) { setSummary(d.summary); setErrors(d.errors); } })
        .catch(() => { if (n === seq.current) setErrors(['Could not check the schedule.']); });
    }, SUMMARY_DELAY_MS);
    return () => clearTimeout(t);
  }, [r, startDate]);

  const set = (patch: Partial<Recurrence>) => setR((prev) => ({ ...prev, ...patch }));
  const num = (v: string, min: number, max: number) => Math.min(max, Math.max(min, Number.parseInt(v, 10) || min));
  const toggleDay = (d: string) => set({ weekDays: r.weekDays.includes(d) ? r.weekDays.filter((x) => x !== d) : [...r.weekDays, d] });
  const oneDay = (d: string) => set({ weekDays: [d] });
  const dis = readOnly;

  const footer = readOnly
    ? <button className="ca-btn" onClick={onCancel}>Close</button>
    : (
      <>
        <button className="ca-btn" onClick={() => onApply(null, 'No recurrence')}>Remove recurrence</button>
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={errors.length > 0 || !summary} onClick={() => onApply(r, summary)}>OK</button>
      </>
    );

  return (
    <AppDialog open title="Recurrence Setup" width={650} onClose={onCancel} closeOnBackdrop={false} footer={footer} className="qcb-dlg">
      <div className="qcb-rec">
        <div className="qcb-rec-freq" role="radiogroup" aria-label="Frequency">
          {FREQS.map((f) => (
            <label key={f}>
              <input type="radio" name="qcb-freq" checked={r.frequency === f} disabled={dis}
                onChange={() => set(f === 'Weekly' && r.weekDays.length === 0 ? { frequency: f, weekDays: ['Mon'] } : { frequency: f })} /> {f}
            </label>
          ))}
        </div>

        <div className="qcb-rec-rule">
          {r.frequency === 'Daily' && (
            <div className="qcb-line">Recur every
              <input className="qcb-in qcb-small" type="number" min={1} value={r.interval} disabled={dis}
                onChange={(e) => set({ interval: num(e.target.value, 1, 999) })} /> day(s)</div>
          )}
          {r.frequency === 'Weekly' && (
            <>
              <div className="qcb-line">Recur every
                <input className="qcb-in qcb-small" type="number" min={1} value={r.interval} disabled={dis}
                  onChange={(e) => set({ interval: num(e.target.value, 1, 999) })} /> week(s) on:</div>
              <div className="qcb-days">
                {DAYS.map((d) => (
                  <label key={d}><input type="checkbox" checked={r.weekDays.includes(d)} disabled={dis} onChange={() => toggleDay(d)} /> {d}</label>
                ))}
              </div>
            </>
          )}
          {r.frequency === 'Monthly' && (
            <>
              <div className="qcb-line">
                <label><input type="radio" name="qcb-mtype" checked={r.pattern !== 'ordinal'} disabled={dis}
                  onChange={() => set({ pattern: 'day' })} /> Day</label>
                <input className="qcb-in qcb-small" type="number" min={1} max={31} value={r.dayNumber} disabled={dis || r.pattern === 'ordinal'}
                  onChange={(e) => set({ dayNumber: num(e.target.value, 1, 31) })} /> of every
                <input className="qcb-in qcb-small" type="number" min={1} value={r.interval} disabled={dis || r.pattern === 'ordinal'}
                  onChange={(e) => set({ interval: num(e.target.value, 1, 999) })} /> month(s)
              </div>
              <div className="qcb-line">
                <label><input type="radio" name="qcb-mtype" checked={r.pattern === 'ordinal'} disabled={dis}
                  onChange={() => set({ pattern: 'ordinal', interval: 1, weekDays: [r.weekDays[0] ?? 'Mon'] })} /> The</label>
                <select className="qcb-in qcb-medium" value={r.ordinal ?? 'First'} disabled={dis || r.pattern !== 'ordinal'}
                  onChange={(e) => set({ ordinal: e.target.value })}>{ORDINALS.map((o) => <option key={o}>{o}</option>)}</select>
                <select className="qcb-in qcb-medium" value={r.weekDays[0] ?? 'Mon'} disabled={dis || r.pattern !== 'ordinal'}
                  onChange={(e) => oneDay(e.target.value)}>{DAYS.map((d, i) => <option key={d} value={d}>{DAY_NAMES[i]}</option>)}</select> of every month
              </div>
            </>
          )}
          {r.frequency === 'Yearly' && (
            <>
              <div className="qcb-line">
                <label><input type="radio" name="qcb-ytype" checked={r.pattern !== 'ordinal'} disabled={dis}
                  onChange={() => set({ pattern: 'day' })} /> Every</label>
                <select className="qcb-in qcb-medium" value={r.month} disabled={dis || r.pattern === 'ordinal'}
                  onChange={(e) => set({ month: Number(e.target.value) })}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
                <input className="qcb-in qcb-small" type="number" min={1} max={31} value={r.dayNumber} disabled={dis || r.pattern === 'ordinal'}
                  onChange={(e) => set({ dayNumber: num(e.target.value, 1, 31) })} />
              </div>
              <div className="qcb-line">
                <label><input type="radio" name="qcb-ytype" checked={r.pattern === 'ordinal'} disabled={dis}
                  onChange={() => set({ pattern: 'ordinal', weekDays: [r.weekDays[0] ?? 'Mon'] })} /> The</label>
                <select className="qcb-in qcb-medium" value={r.ordinal ?? 'First'} disabled={dis || r.pattern !== 'ordinal'}
                  onChange={(e) => set({ ordinal: e.target.value })}>{ORDINALS.map((o) => <option key={o}>{o}</option>)}</select>
                <select className="qcb-in qcb-medium" value={r.weekDays[0] ?? 'Mon'} disabled={dis || r.pattern !== 'ordinal'}
                  onChange={(e) => oneDay(e.target.value)}>{DAYS.map((d, i) => <option key={d} value={d}>{DAY_NAMES[i]}</option>)}</select> of
                <select className="qcb-in qcb-medium" value={r.month} disabled={dis || r.pattern !== 'ordinal'}
                  onChange={(e) => set({ month: Number(e.target.value) })}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="qcb-rec-ends">
        {(['never', 'after', 'by'] as Ends[]).map((e) => (
          <div className="qcb-line" key={e}>
            <label><input type="radio" name="qcb-ends" checked={r.ends === e} disabled={dis} onChange={() => set({ ends: e })} />
              {e === 'never' ? ' No end date' : e === 'after' ? ' End after:' : ' End by:'}</label>
            {e === 'after' && (<>
              <input className="qcb-in qcb-small" type="number" min={1} value={r.occurrences} disabled={dis || r.ends !== 'after'}
                onChange={(ev) => set({ occurrences: num(ev.target.value, 1, 9999) })} /> occurrences</>)}
            {e === 'by' && (
              <input className="qcb-in qcb-medium" type="date" value={r.endBy ?? ''} disabled={dis || r.ends !== 'by'}
                onChange={(ev) => set({ endBy: ev.target.value || null })} />)}
          </div>
        ))}
      </div>

      <div className="qcb-rec-summary"><b>Schedule:</b> {summary}</div>
      {errors.length > 0 && <div className="qcb-fx-msg err">{errors.join(' ')}</div>}
    </AppDialog>
  );
}

/** Fill the fields a stored schedule leaves empty, so every control has a value. */
function normalise(r: Recurrence): Recurrence {
  return {
    ...r,
    interval: r.interval || 1,
    weekDays: r.weekDays?.length ? r.weekDays : ['Mon'],
    pattern: r.pattern ?? 'day',
    dayNumber: r.dayNumber || 1,
    ordinal: r.ordinal ?? 'First',
    month: r.month || 1,
    occurrences: r.occurrences || 10,
  };
}
